import { useState, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import CustomBtn from "../../Components/Reusable/CustomBtn";
import SelectCustom from "../../Components/Inputs/SelectCustom";
import Holder from "../../Components/HOC/Holder";
import LoadBtn from "../../Components/Reusable/LoadBtn";
import InputTextCustom from "../../Components/Inputs/InputTextCustom";
import axios from "axios";
import { baseURL, streamTypesArr } from "../../utils/StaticVariables";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import useFetchStreams from "../../hooks/useFetchStreams";

const AddStream = () => {
  const { token } = useRecoilValue(authState);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { refetchStreams } = useFetchStreams();

  const formik = useFormik({
    initialValues: {
      name: "",
      path: "",
      type: streamTypesArr[0],
      alert_enabled: true,
      area: "",
      building: "",
      count_threshold_greater: 0,
      count_threshold_less: 0,
      floor_level: "",
      is_streaming: true,
      latitude: "",
      location: "",
      longitude: "",
      status: "active",
      zone: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .min(2, "Name must be at least 2 characters"),
      path: Yup.string().required("Path is required"),
      type: Yup.string()
        .oneOf(streamTypesArr, "Invalid type")
        .required("Type is required"),
      area: Yup.string().required("Area is required"),
      building: Yup.string().required("Building is required"),
      count_threshold_greater: Yup.number().min(0).required(),
      count_threshold_less: Yup.number().min(0).required(),
      floor_level: Yup.string().required("Floor level is required"),
      latitude: Yup.number().required("Latitude is required"),
      longitude: Yup.number().required("Longitude is required"),
      location: Yup.string().required("Location is required"),
      status: Yup.string().oneOf(["active", "inactive"]).required(),
      zone: Yup.string().required("Zone is required"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      axios
        .post(`${baseURL}source`, values, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          formik.handleReset();
          refetchStreams();
          enqueueSnackbar("Your new stream added successfully", {
            variant: "success",
          });
        })
        .catch((error) => {
          enqueueSnackbar(error?.message || "Something went wrong", {
            variant: "error",
          });
        })
        .finally(() => setLoading(false));
    },
  });

  const handleBulkUpload = () => {
    if (!csvFile) {
      enqueueSnackbar("Please select a CSV file first", { variant: "warning" });
      return;
    }

    const formData = new FormData();
    formData.append("file", csvFile);

    setBulkLoading(true);
    axios
      .post(`${baseURL}source/bulk-upload-with-location`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        enqueueSnackbar("Bulk upload completed successfully", {
          variant: "success",
        });
        refetchStreams();
        setCsvFile(null);
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || "Bulk upload failed", {
          variant: "error",
        });
      })
      .finally(() => setBulkLoading(false));
  };

  return (
    <Holder title="add stream">

      {/* رفع CSV */}
      <Box mb={4}>
        <Typography mb={1} fontWeight="bold">
          Bulk Upload CSV
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CustomBtn handle={() => fileInputRef.current.click()}>
            Choose CSV File
          </CustomBtn>
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => setCsvFile(e.target.files[0])}
          />
          {csvFile && (
            <Typography variant="body2" sx={{ maxWidth: "200px" }} noWrap>
              {csvFile.name}
            </Typography>
          )}
          <CustomBtn handle={handleBulkUpload}>
            {bulkLoading ? "Uploading..." : "Upload"}
          </CustomBtn>
        </Stack>
      </Box>

      {/* الفورم */}
      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        <InputTextCustom formik={formik} name="name" label="Name" placeholder="Stream Name" />
        <InputTextCustom formik={formik} name="path" label="Source" placeholder="Stream Path" />
        <SelectCustom formik={formik} arr={streamTypesArr} name="type" label="Type" />
        <InputTextCustom formik={formik} name="area" label="Area" placeholder="Area" />
        <InputTextCustom formik={formik} name="building" label="Building" placeholder="Building" />
        <InputTextCustom formik={formik} name="count_threshold_greater" label="Count Threshold Greater" placeholder="0" />
        <InputTextCustom formik={formik} name="count_threshold_less" label="Count Threshold Less" placeholder="0" />
        <InputTextCustom formik={formik} name="floor_level" label="Floor Level" placeholder="Floor Level" />
        <InputTextCustom formik={formik} name="latitude" label="Latitude" placeholder="Latitude" />
        <InputTextCustom formik={formik} name="longitude" label="Longitude" placeholder="Longitude" />
        <InputTextCustom formik={formik} name="location" label="Location" placeholder="Location" />
        <InputTextCustom formik={formik} name="zone" label="Zone" placeholder="Zone" />
        <SelectCustom formik={formik} arr={["active", "inactive"]} name="status" label="Status" />

        <Stack alignItems="center" justifyContent="space-between" direction="row" mt={4}>
          <CustomBtn isLined handle={() => navigate("/streams")}>Back</CustomBtn>
          <Stack direction="row" gap={2}>
            <CustomBtn isLined handle={formik.handleReset}>Clear</CustomBtn>
            <LoadBtn loading={loading} />
          </Stack>
        </Stack>
      </Box>
    </Holder>
  );
};

export default AddStream;
